"use client";

import { TierSlateTier, USDC_SCALE } from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import type { LockFundingSource } from "@/contexts/mutations/funded-trial/lock";
import { NumberFormatter } from "@/utils/number-formatter";

import { DynamicOutputCard } from "./dynamic-output-card";
import { LeversSummary } from "./levers-summary";
import { LockAmountField } from "./lock-amount-field";
import { LockDurationButtons } from "./lock-duration-buttons";
import { LockFundingRow } from "./lock-funding-row";

export interface LeversPaneProps {
  amount: bigint;
  onAmountChange: (next: bigint) => void;
  minLockAmount: bigint;
  maxLockAmount: bigint;
  durationDays: number;
  onDurationChange: (next: number) => void;
  tierSlate: readonly TierSlateTier[];
  creditsRemaining: number;
  creditsTotal: number;
  tradePositionUsd: number;
  ctaLabel: string;
  ctaDisabled: boolean;
  ctaLoading: boolean;
  onCtaClick: () => void;
  trialMinutes: number;
  /** Shown as a read-only "From:" row while the lock is editable. */
  fundingSource?: LockFundingSource;
  /** Post-lock: the levers reflect the locked config and can't be edited. */
  leversDisabled?: boolean;
  alert?: React.ReactNode;
}

export function LeversPane({
  amount,
  onAmountChange,
  minLockAmount,
  maxLockAmount,
  durationDays,
  onDurationChange,
  tierSlate,
  creditsRemaining,
  creditsTotal,
  tradePositionUsd,
  ctaLabel,
  ctaDisabled,
  ctaLoading,
  onCtaClick,
  trialMinutes,
  fundingSource,
  leversDisabled = false,
  alert,
}: LeversPaneProps) {
  const t = useTranslations("firstTradeOnUs.levers");

  const amountUsd = Number(amount) / USDC_SCALE;
  const lockAmountLabel = NumberFormatter.usdc(amountUsd, {
    decimals: Number.isInteger(amountUsd) ? 0 : 2,
  });
  const durationLabel = t("durationDays", { days: durationDays });

  return (
    <div className={containerStyles} data-testid="fft-levers-pane">
      {alert}
      <div className={leversBoxStyles}>
        <p className={helperTextStyles}>{t("helper")}</p>
        <LockAmountField
          amount={amount}
          onAmountChange={onAmountChange}
          minLockAmount={minLockAmount}
          maxLockAmount={maxLockAmount}
          disabled={leversDisabled}
          label={leversDisabled ? t("lockedAmountLabel") : undefined}
        />
        {fundingSource !== undefined && !leversDisabled && (
          <LockFundingRow source={fundingSource} />
        )}
        <LockDurationButtons
          durationDays={durationDays}
          onDurationChange={onDurationChange}
          disabled={leversDisabled}
          tierSlate={tierSlate}
        />
      </div>
      <DynamicOutputCard
        creditsRemaining={creditsRemaining}
        creditsTotal={creditsTotal}
        tradePositionUsd={tradePositionUsd}
      />
      <LeversSummary
        lockAmountLabel={lockAmountLabel}
        durationLabel={durationLabel}
        trialMinutes={trialMinutes}
      />
      <div className={spacerStyles} />
      <Button
        variant="primary"
        size="md"
        fullWidth
        loading={ctaLoading}
        disabled={ctaDisabled}
        onClick={onCtaClick}
        data-testid="fft-levers-cta"
      >
        {ctaLabel}
      </Button>
    </div>
  );
}

const containerStyles = stack({
  gap: "16",
  p: "16",
  rounded: "200",
  border: "1px solid",
  borderColor: "card.border",
  bg: "card.background",
  w: { base: "full", lg: "[419px]" },
  flexShrink: 0,
});

const leversBoxStyles = stack({
  gap: "16",
  p: "12",
  rounded: "350",
  border: "1px solid",
  borderColor: "card.border",
});

const helperTextStyles = css({ textStyle: "highlight.md", color: "text.primary" });

const spacerStyles = css({ flexGrow: 1 });
